import { useModal } from "@ebay/nice-modal-react";
import { t } from "@/i18n-js/instance";
import { ModalManager } from "@/react/components/Layout/ModalManagerProvider";
import { useCurrentCommunityMember } from "@/react/contexts/punditUserContext";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { useProfileLinkProps } from "@/react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { MomentumModal } from "@circle-react-shared/ModalsV2/MomentumModal";
import { Typography } from "@circle-react-uikit/Typography";
import { Icon } from "../../../Icon";
import { Button } from "../../../uikit/Button";
import { SanitizeHTMLString } from "../../../uikit/SanitizeContent";
import { useSendMessageToUser } from "../../hooks/useSendMessageToUser";
import { MemberCardImage } from "../CardView/MemberCardImage";

const i18nBase = "member_directory.accept_connection_momentum_modal";

interface AcceptConnectionMomentumModalComponentProps {
  recipient: CommunityMember;
}

export const AcceptConnectionMomentumModal = ModalManager.create(
  function AcceptConnectionMomentumModal({
    recipient,
  }: AcceptConnectionMomentumModalComponentProps) {
    const modal = useModal();
    const currentCommunityMember = useCurrentCommunityMember();
    const { handleMessageMember, isLoading } = useSendMessageToUser();
    const isAdmin = isCommunityAdmin(currentCommunityMember);

    const profileLinkProps = useProfileLinkProps({
      public_uid: recipient.public_uid ?? "",
    });

    const isRecipientMessagingEnabled = recipient.messaging_enabled || isAdmin;

    const handleClose = () => {
      void modal.hide();
    };

    return (
      <MomentumModal
        isOpen={modal.visible}
        onClose={handleClose}
        title=""
        description=""
        data-testid="accept-connection-momentum-modal"
        bodyClassName="!gap-y-0"
        headerClassName="!border-none"
      >
        <div className="flex flex-row gap-x-2">
          <div className="flex w-40 flex-col items-center rounded-2xl border px-6 py-4 text-center">
            <div style={{ aspectRatio: "1 / 1" }} className="mb-3 h-14">
              <MemberCardImage
                name={currentCommunityMember?.name ?? ""}
                src={currentCommunityMember?.avatar_url ?? ""}
                className="!rounded-full"
              />
            </div>
            <div className="w-full">
              <Typography.TitleSm weight="semibold" size="text-sm" truncate>
                {currentCommunityMember?.name}
              </Typography.TitleSm>
            </div>
            <div className="w-full">
              <Typography.BodySm truncate size="text-xs" color="text-secondary">
                {currentCommunityMember?.headline}
              </Typography.BodySm>
            </div>
          </div>

          <div className="flex w-40 flex-col items-center rounded-2xl border px-6 py-4 text-center">
            <div style={{ aspectRatio: "1 / 1" }} className="mb-3 h-14">
              <MemberCardImage
                name={recipient.name ?? ""}
                src={recipient.avatar_url ?? ""}
                className="!rounded-full"
              />
            </div>

            <div className="w-full">
              <Typography.TitleSm weight="semibold" size="text-sm" truncate>
                {recipient.name}
              </Typography.TitleSm>
            </div>
            <div className="w-full">
              <Typography.BodySm truncate size="text-xs" color="text-secondary">
                {recipient.headline}
              </Typography.BodySm>
            </div>
          </div>
        </div>
        <div className="mt-10 flex w-full flex-col items-center gap-y-3">
          <Typography.TitleLg size="text-2xl">
            {t(`${i18nBase}.title`)}
          </Typography.TitleLg>
          <Typography.LabelMd color="text-secondary">
            <SanitizeHTMLString
              content={t(`${i18nBase}.body`, { recipient: recipient.name })}
              className="block w-full text-center leading-6 tracking-tighter"
            />
          </Typography.LabelMd>
        </div>
        <div className="mt-12 flex flex-col">
          {isRecipientMessagingEnabled ? (
            <Button
              variant="secondary"
              onClick={async () => {
                await handleMessageMember(recipient.id.toString());
                handleClose();
              }}
              aria-label={t("message")}
              data-testid="chat-button"
              large
              className="!border-secondary !h-10 w-full gap-2 !px-5 !font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                <Icon type="loader" viewBox="0 0 20 20" />
              ) : (
                <>
                  <Icon
                    type="20-bubble-annotation"
                    size={20}
                    useWithFillCurrentColor
                  />
                  <Typography.LabelMd color="text-primary" weight="!font-bold">
                    {t(`${i18nBase}.cta`, { recipient: recipient.name })}
                  </Typography.LabelMd>
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={event => {
                profileLinkProps.onClick(event);
                handleClose();
              }}
              aria-label={t("message")}
              data-testid="chat-button"
              large
              className="!border-secondary !h-10 w-full gap-2 !px-5 !font-medium"
            >
              <>
                <Icon
                  type="20-bubble-annotation"
                  size={20}
                  useWithFillCurrentColor
                />
                <Typography.LabelMd color="text-primary" weight="!font-bold">
                  {t(`${i18nBase}.cta`, { recipient: recipient.name })}
                </Typography.LabelMd>
              </>
            </Button>
          )}
        </div>
      </MomentumModal>
    );
  },
);
